/**
 * Izgara pass: yalnız stride pikselleri. Fill pass: upsample; okCount<3 ise o anda computeGi.
 */

import { NearestFilter } from 'three';
import { RenderTarget, Vector2, TempNode, QuadMesh, NodeMaterial, RendererUtils, MathUtils } from 'three/webgpu';
import { clamp, normalize, reference, Fn, NodeUpdateType, uniform, vec4, passTexture, uv, logarithmicDepthToViewZ, viewZToPerspectiveDepth, getViewPosition, screenCoordinate, float, sub, fract, dot, vec2, rand, vec3, Loop, mul, PI, cos, sin, uint, cross, acos, sign, pow, luminance, If, max, min, abs, Break, sqrt, HALF_PI, div, ceil, floor, mod, shiftRight, convertToTexture, bool, getNormalFromDepth, countOneBits, interleavedGradientNoise, Discard } from 'three/tsl';
import { upsampleSparseGi } from './sparseGiUpsample.ts';
import { depthEdgeMask } from './sparseGiDepthEdge.ts';
import { parentCellNeedsRefine } from './sparseGiCellRefine.ts';

const _sparseGiQuad = /*@__PURE__*/ new QuadMesh();
const _size = /*@__PURE__*/ new Vector2();

// From Activision GTAO paper: https://www.activision.com/cdn/research/s2016_pbs_activision_occlusion.pptx
const _temporalRotations = [ 60, 300, 180, 240, 120, 0 ];
const _spatialOffsets = [ 0, 0.5, 0.25, 0.75 ];
// TRAA açıksa frameId ile döner: slice açısı (6 değer) + ilk adım offset (Bayer 4).

let _rendererState;

/**
 * Post processing node for applying Screen Space Global Illumination (SSGI) to a scene.
 *
 * References:
 * - {@link https://github.com/cdrinmatane/SSRT3}.
 * - {@link https://cdrinmatane.github.io/posts/ssaovb-code/}.
 * - {@link https://cdrinmatane.github.io/cgspotlight-slides/ssilvb_slides.pdf}.
 *
 * The quality and performance of the effect mainly depend on `sliceCount` and `stepCount`.
 * The total number of samples taken per pixel is `sliceCount` * `stepCount` * `2`. Here are some
 * recommended presets depending on whether temporal filtering is used or not.
 *
 * With temporal filtering (recommended):
 *
 * - Low: `sliceCount` of `1`, `stepCount` of `12`.
 * - Medium: `sliceCount` of `2`, `stepCount` of `8`.
 * - High: `sliceCount` of `3`, `stepCount` of `16`.
 *
 * Use for a higher slice count if you notice temporal instabilities like flickering. Reduce the sample
 * count then to mitigate the performance lost.
 *
 * Without temporal filtering:
 *
 * - Low: `sliceCount` of `2`, `stepCount` of `6`.
 * - Medium: `sliceCount` of `3`, `stepCount` of `8`.
 * - High: `sliceCount` of `4`, `stepCount` of `12`.
 *
 * @augments TempNode
 * @three_import import { ssgi } from 'three/addons/tsl/display/SSGINode.js';
 */
class SparseGiNode extends TempNode {

	static get type() {

		return 'SparseGiNode';

	}

	/**
	 * Constructs a new SSGI node.
	 *
	 * @param {TextureNode} beautyNode - A texture node that represents the beauty or scene pass.
	 * @param {TextureNode} depthNode - A texture node that represents the scene's depth.
	 * @param {TextureNode} normalNode - A texture node that represents the scene's normals.
	 * @param {PerspectiveCamera} camera - The camera the scene is rendered with.
	 */
	constructor( beautyNode, depthNode, normalNode, camera ) {

		super( 'vec4' );

		/**
		 * A texture node that represents the beauty or scene pass.
		 *
		 * @type {TextureNode}
		 */
		this.beautyNode = beautyNode;

		/**
		 * A node that represents the scene's depth.
		 *
		 * @type {TextureNode}
		 */
		this.depthNode = depthNode;

		/**
		 * A node that represents the scene's normals. If no normals are passed to the
		 * constructor (because MRT is not available), normals can be automatically
		 * reconstructed from depth values in the shader.
		 *
		 * @type {TextureNode}
		 */
		this.normalNode = normalNode;

		/**
		 * The `updateBeforeType` is set to `NodeUpdateType.FRAME` since the node renders
		 * its effect once per frame in `updateBefore()`.
		 *
		 * @type {string}
		 * @default 'frame'
		 */
		this.updateBeforeType = NodeUpdateType.FRAME;
		// TSL grafiğinden önce her kare bu pass'i çalıştır (ayrı RT).

		/**
		 * Number of per-pixel hemisphere slices. This has a big performance cost and should be kept as low as possible.
		 * Should be in the range `[1, 4]`.
		 *
		 * @type {UniformNode<uint>}
		 * @default 1
		 */
		this.sliceCount = uniform( 1, 'uint' );

		/**
		 * Number of samples taken along one side of a given hemisphere slice. This has a big performance cost and should
		 * be kept as low as possible.  Should be in the range `[1, 32]`.
		 *
		 * @type {UniformNode<uint>}
		 * @default 12
		 */
		this.stepCount = uniform( 12, 'uint' );

		/**
		 * Power function applied to AO to make it appear darker/lighter. Should be in the range `[0, 4]`.
		 *
		 * @type {UniformNode<float>}
		 * @default 1
		 */
		this.aoIntensity = uniform( 1, 'float' );

		/**
		 * Intensity of the indirect diffuse light. Should be in the range `[0, 100]`.
		 *
		 * @type {UniformNode<float>}
		 * @default 10
		 */
		this.giIntensity = uniform( 10, 'float' );

		/**
		 * Effective sampling radius in world space. AO and GI can only have influence within that radius.
		 * Should be in the range `[1, 25]`.
		 *
		 * @type {UniformNode<float>}
		 * @default 12
		 */
		this.radius = uniform( 12, 'float' );

		/**
		 * Makes the sample distance in screen space instead of world-space (helps having more detail up close).
		 *
		 * @type {UniformNode<bool>}
		 * @default false
		 */
		this.useScreenSpaceSampling = uniform( true, 'bool' );

		/**
		 * Controls samples distribution. It's an exponent applied at each step get increasing step size over the distance.
		 * Should be in the range `[1, 3]`.
		 *
		 * @type {UniformNode<float>}
		 * @default 2
		 */
		this.expFactor = uniform( 2, 'float' );

		/**
		 * Constant thickness value of objects on the screen in world units. Allows light to pass behind surfaces past that thickness value.
		 * Should be in the range `[0.01, 10]`.
		 *
		 * @type {UniformNode<float>}
		 * @default 1
		 */
		this.thickness = uniform( 1, 'float' );

		/**
		 * Whether to increase thickness linearly over distance or not (avoid losing detail over the distance).
		 *
		 * @type {UniformNode<bool>}
		 * @default false
		 */
		this.useLinearThickness = uniform( false, 'bool' );

		/**
		 * How much light backface surfaces emit.
		 * Should be in the range `[0, 1]`.
		 *
		 * @type {UniformNode<float>}
		 * @default 0
		 */
		this.backfaceLighting = uniform( 0, 'float' );

		/**
		 * Whether to use temporal filtering or not. Setting this property to
		 * `true` requires the usage of `TRAANode`. This will help to reduce noise
		 * although it introduces typical TAA artifacts like ghosting and temporal
		 * instabilities.
		 *
		 * If setting this property to `false`, a manual denoise via `DenoiseNode`
		 * is required.
		 *
		 * @type {boolean}
		 * @default true
		 */
		this.useTemporalFiltering = true;

		// private uniforms

		/**
		 * The resolution of the effect.
		 *
		 * @private
		 * @type {UniformNode<vec2>}
		 */
		this._resolution = uniform( new Vector2() );
		this.giStride = 1;
		this.fullResolution = uniform( new Vector2() );
		this._strideUniform = uniform( 1 );
		this._reversedDepthUniform = uniform( 0 );
		this.depthThreshold = uniform( 0.25 );
		this.normalThreshold = uniform( 0.8 );
		this.minOkCount = uniform( 3 );
		this.edgeThickness = uniform( 2 );
		this.edgeKernel = uniform( 1 );
		this.edgeThreshold = uniform( 0.04 );
		this.adaptiveMode = 'off';
		this.debugView = uniform( 0 );

		/**
		 * Used to compute the effective step radius when viewSpaceSampling is `false`.
		 *
		 * @private
		 * @type {UniformNode<vec2>}
		 */
		this._halfProjScale = uniform( 1 );

		/**
		 * Temporal direction that influences the rotation angle for each slice.
		 *
		 * @private
		 * @type {UniformNode<float>}
		 */
		this._temporalDirection = uniform( 0 );

		/**
		 * Temporal offset added to the initial ray step.
		 *
		 * @private
		 * @type {UniformNode<float>}
		 */
		this._temporalOffset = uniform( 0 );

		/**
		 * Represents the projection matrix of the scene's camera.
		 *
		 * @private
		 * @type {UniformNode<mat4>}
		 */
		this._cameraProjectionMatrix = uniform( camera.projectionMatrix );

		/**
		 * Represents the inverse projection matrix of the scene's camera.
		 *
		 * @private
		 * @type {UniformNode<mat4>}
		 */
		this._cameraProjectionMatrixInverse = uniform( camera.projectionMatrixInverse );

		/**
		 * Represents the near value of the scene's camera.
		 *
		 * @private
		 * @type {ReferenceNode<float>}
		 */
		this._cameraNear = reference( 'near', 'float', camera );

		/**
		 * Represents the far value of the scene's camera.
		 *
		 * @private
		 * @type {ReferenceNode<float>}
		 */
		this._cameraFar = reference( 'far', 'float', camera );

		/**
		 * A reference to the scene's camera.
		 *
		 * @private
		 * @type {PerspectiveCamera}
		 */
		this._camera = camera;

		/**
		 * The render target the GI is rendered into.
		 *
		 * @private
		 * @type {RenderTarget}
		 */
		// GI+AO buraya yazılır. Temporal history buffer yok.
		this._ssgiRenderTarget = new RenderTarget( 1, 1, { depthBuffer: false } );
		this._ssgiRenderTarget.texture.name = 'SparseGI';
		this._ssgiRenderTarget.texture.minFilter = NearestFilter;
		this._ssgiRenderTarget.texture.magFilter = NearestFilter;

		this._fillTarget = new RenderTarget( 1, 1, { depthBuffer: false } );
		this._fillTarget.texture.name = 'SparseGIFill';
		this._fillTarget.texture.minFilter = NearestFilter;
		this._fillTarget.texture.magFilter = NearestFilter;

		this._material = new NodeMaterial();
		this._material.name = 'SparseGI';
		this._fillMaterial = new NodeMaterial();
		this._fillMaterial.name = 'SparseGIFill';

		this._textureNode = passTexture( this, this._fillTarget.texture );

	}

	/**
	 * Returns the result of the effect as a texture node.
	 *
	 * @return {PassTextureNode} A texture node that represents the result of the effect.
	 */
	getTextureNode() {

		return this._textureNode;

	}

	/**
	 * Sets the size of the effect.
	 *
	 * @param {number} width - The width of the effect.
	 * @param {number} height - The height of the effect.
	 */
	setSize( width, height ) {

		const stride = Math.max( 1, Math.floor( Number( this.giStride ) ) || 1 );
		this._strideUniform.value = stride;
		this.fullResolution.value.set( width, height );
		this._resolution.value.set( width, height );
		this._ssgiRenderTarget.setSize( width, height );
		this._fillTarget.setSize( width, height );
		this._halfProjScale.value = height / ( Math.tan( this._camera.fov * MathUtils.DEG2RAD * 0.5 ) * 2 ) * 0.5;

	}

	/**
	 * This method is used to render the effect once per frame.
	 *
	 * @param {NodeFrame} frame - The current node frame.
	 */
	updateBefore( frame ) {

		const { renderer } = frame;

		_rendererState = RendererUtils.resetRendererState( renderer, _rendererState );

		//

		const size = renderer.getDrawingBufferSize( _size );
		this.setSize( size.width, size.height );

		// update temporal uniforms

		if ( this.useTemporalFiltering === true ) {

			const frameId = frame.frameId;

			this._temporalDirection.value = _temporalRotations[ frameId % 6 ] / 360;
			this._temporalOffset.value = _spatialOffsets[ frameId % 4 ];
			// Jitter only — önceki kare toplanmaz. Kapalıyken 1,1: patern donar.

		} else {

			this._temporalDirection.value = 1;
			this._temporalOffset.value = 1;

		}

		//

		_sparseGiQuad.material = this._material;
		_sparseGiQuad.name = 'SparseGI';
		renderer.setClearColor( 0x000000, 0 );
		renderer.setRenderTarget( this._ssgiRenderTarget );
		renderer.clear();
		_sparseGiQuad.render( renderer );

		_sparseGiQuad.material = this._fillMaterial;
		_sparseGiQuad.name = 'SparseGIFill';
		renderer.setRenderTarget( this._fillTarget );
		renderer.clear();
		_sparseGiQuad.render( renderer );

		// restore

		RendererUtils.restoreRendererState( renderer, _rendererState );

	}

	/**
	 * This method is used to setup the effect's TSL code.
	 *
	 * @param {NodeBuilder} builder - The current node builder.
	 * @return {PassTextureNode}
	 */
	setup( builder ) {

		const uvNode = uv();
		this._reversedDepthUniform.value = builder.renderer.reversedDepthBuffer === true ? 1 : 0;
		const MAX_RAY = uint( 32 ); // hemisfer 32 açı sektörü (bitfield)
		const globalOccludedBitfield = uint( 0 ); // slice boyunca biriken "görünür oklüzyon" bitleri

		// Hardware depth → perspective depth (log buffer ise viewZ üzerinden).
		const sampleDepth = ( uv ) => {

			const depth = this.depthNode.sample( uv ).r;

			if ( builder.renderer.logarithmicDepthBuffer === true ) {

				const viewZ = logarithmicDepthToViewZ( depth, this._cameraNear, this._cameraFar );

				return viewZToPerspectiveDepth( viewZ, this._cameraNear, this._cameraFar );

			}

			return depth;

		};

		// MRT normal yoksa depth'ten türet. Beauty = direkt lit (GI kaynağı).
		const sampleNormal = ( uv ) => ( this.normalNode !== null ) ? this.normalNode.sample( uv ).rgb.normalize() : getNormalFromDepth( uv, this.depthNode.value, this._cameraProjectionMatrixInverse );
		const sampleBeauty = ( uv ) => this.beautyNode.sample( uv );

		// From Activision GTAO paper: https://www.activision.com/cdn/research/s2016_pbs_activision_occlusion.pptx

		// GTAO Bayer-benzeri 2x2 spatial offset (piksel koordinatına göre).
		const spatialOffsets = Fn( ( [ position ] ) => {

			return float( 0.25 ).mul( sub( position.y, position.x ).bitAnd( 3 ) );

		} ).setLayout( {
			name: 'spatialOffsets',
			type: 'float',
			inputs: [
				{ name: 'position', type: 'vec2' }
			]
		} );

		// ucos yaklaşımı (vec2): horizon açısı için ucuz acos.
		const GTAOFastAcos = Fn( ( [ value ] ) => {

			const outVal = abs( value ).mul( float( - 0.156583 ) ).add( HALF_PI );
			outVal.mulAssign( sqrt( abs( value ).oneMinus() ) );

			const x = value.x.greaterThanEqual( 0 ).select( outVal.x, PI.sub( outVal.x ) );
			const y = value.y.greaterThanEqual( 0 ).select( outVal.y, PI.sub( outVal.y ) );

			return vec2( x, y );

		} ).setLayout( {
			name: 'GTAOFastAcos',
			type: 'vec2',
			inputs: [
				{ name: 'value', type: 'vec2' }
			]
		} );

		// Bir slice yarısı: UV'de yürüyüş, horizon + GI. directionIsRight = sağ/sol.
		const horizonSampling = Fn( ( [ directionIsRight, RADIUS, viewPosition, slideDirTexelSize, initialRayStep, uvNode, viewDir, viewNormal, n ] ) => {

			const STEP_COUNT = this.stepCount.toConst();
			const EXP_FACTOR = this.expFactor.toConst();
			const THICKNESS = this.thickness.toConst();
			const BACKFACE_LIGHTING = this.backfaceLighting.toConst();

			const stepRadius = float( 0 );

			If( this.useScreenSpaceSampling.equal( true ), () => {

				// Screen-space: radius çözünürlüğe bağlı UV yarıçapı (yakında daha sık).
				stepRadius.assign( RADIUS.mul( this._resolution.x.div( 2 ) ).div( float( 16 ) ) ); // SSRT3 has a bug where stepRadius is divided by STEP_COUNT twice; fix here

			} ).Else( () => {

				// World-space: |viewZ| büyüdükçe ekrandaki adım küçülür.
				stepRadius.assign( max( RADIUS.mul( this._halfProjScale ).div( viewPosition.z.negate() ), float( STEP_COUNT ) ) ); // Port note: viewZ is negative so a negate is required

			} );

			stepRadius.divAssign( float( STEP_COUNT ).add( 1 ) );
			const radiusVS = max( 1, float( STEP_COUNT.sub( 1 ) ) ).mul( stepRadius );
			const uvDirection = directionIsRight.equal( true ).select( vec2( 1, - 1 ), vec2( - 1, 1 ) ); // Port note: Because of different uv conventions, uv-y has a different sign
			const samplingDirection = directionIsRight.equal( true ).select( 1, - 1 );

			const color = vec3( 0 );

			Loop( { start: uint( 0 ), end: STEP_COUNT, type: 'uint', condition: '<' }, ( { i } ) => {

				// t^expFactor: yakın adımlar sık, uzak seyrek.
				const offset = pow( abs( mul( stepRadius, float( i ).add( initialRayStep ) ).div( radiusVS ) ), EXP_FACTOR ).mul( radiusVS ).toConst();
				const uvOffset = slideDirTexelSize.mul( max( offset, float( i ).add( 1 ) ) ).toConst();
				const sampleUV = uvNode.add( uvOffset.mul( uvDirection ) ).toConst();

				If( sampleUV.x.lessThanEqual( 0 ).or( sampleUV.y.lessThanEqual( 0 ) ).or( sampleUV.x.greaterThanEqual( 1 ) ).or( sampleUV.y.greaterThanEqual( 1 ) ), () => {

					Break();

				} );

				// Hit: o UV'deki depth → view-space. Thickness: yüzeyin "arkası" (ışık sızması).
				const sampleViewPosition = getViewPosition( sampleUV, sampleDepth( sampleUV ), this._cameraProjectionMatrixInverse ).toConst();
				const pixelToSample = sampleViewPosition.sub( viewPosition ).normalize().toConst();
				const linearThicknessMultiplier = this.useLinearThickness.equal( true ).select( sampleViewPosition.z.negate().div( this._cameraFar ).clamp().mul( 100 ), float( 1 ) );
				const pixelToSampleBackface = normalize( sampleViewPosition.sub( linearThicknessMultiplier.mul( viewDir ).mul( THICKNESS ) ).sub( viewPosition ) );

				// Ön/arka horizon açıları → 0..1 dilim; n projected-normal ofseti.
				let frontBackHorizon = vec2( dot( pixelToSample, viewDir ), dot( pixelToSampleBackface, viewDir ) );
				frontBackHorizon = GTAOFastAcos( clamp( frontBackHorizon, - 1, 1 ) );
				frontBackHorizon = clamp( div( mul( samplingDirection, frontBackHorizon.negate() ).sub( n.sub( HALF_PI ) ), PI ) ); // Port note: subtract half pi instead of adding it
				frontBackHorizon = directionIsRight.equal( true ).select( frontBackHorizon.yx, frontBackHorizon.xy ); // Front/Back get inverted depending on angle

				// inline ComputeOccludedBitfield() for easier debugging

				const minHorizon = frontBackHorizon.x.toConst();
				const maxHorizon = frontBackHorizon.y.toConst();

				// Horizon aralığını 32 bite yaz; sadece henüz kapanmamış bitler "yeni görünür".
				const startHorizonInt = uint( frontBackHorizon.mul( float( MAX_RAY ) ) ).toConst();
				const angleHorizonInt = uint( ceil( maxHorizon.sub( minHorizon ).mul( float( MAX_RAY ) ) ) ).toConst();
				const angleHorizonBitfield = angleHorizonInt.greaterThan( uint( 0 ) ).select( uint( shiftRight( uint( 0xFFFFFFFF ), uint( 32 ).sub( MAX_RAY ).add( MAX_RAY.sub( angleHorizonInt ) ) ) ), uint( 0 ) ).toConst();
				let currentOccludedBitfield = angleHorizonBitfield.shiftLeft( startHorizonInt );
				currentOccludedBitfield = currentOccludedBitfield.bitAnd( globalOccludedBitfield.bitNot() );

				globalOccludedBitfield.assign( globalOccludedBitfield.bitOr( currentOccludedBitfield ) );
				const numOccludedZones = countOneBits( currentOccludedBitfield );

				//

				// Yeni sektör > 0 → bu sample shading noktasından görünür; GI buradan.
				If( numOccludedZones.greaterThan( 0 ), () => { // If a ray hit the sample, that sample is visible from shading point

					const lightColor = sampleBeauty( sampleUV );

					If( luminance( lightColor ).greaterThan( 0.001 ), () => { // Continue if there is light at that location (intensity > 0)

						const lightDirectionVS = normalize( pixelToSample );
						const normalDotLightDirection = clamp( dot( viewNormal, lightDirectionVS ) );

						If( normalDotLightDirection.greaterThan( 0.001 ), () => { // Continue if light is facing surface normal

							const lightNormalVS = sampleNormal( sampleUV );

							// Intensity of outgoing light in the direction of the shading point

							let lightNormalDotLightDirection = dot( lightNormalVS, lightDirectionVS.negate() );

							const d = sign( lightNormalDotLightDirection ).lessThan( 0 ).select( abs( lightNormalDotLightDirection ).mul( BACKFACE_LIGHTING ), abs( lightNormalDotLightDirection ) );
							lightNormalDotLightDirection = BACKFACE_LIGHTING.greaterThan( 0 ).and( dot( lightNormalVS, viewDir ).greaterThan( 0 ) ).select( d, clamp( lightNormalDotLightDirection ) );

							// Ağırlık: (yeni bit / 32) * beauty * N·L * (hitN · -L). BackfaceLighting arkadan sızdırır.
							color.rgb.addAssign( float( numOccludedZones ).div( float( MAX_RAY ) ).mul( lightColor ).mul( normalDotLightDirection ).mul( lightNormalDotLightDirection ) );

						} );

					} );

				} );

			} );

			return vec3( color );

		} );

		const rawDepth = this.depthNode.sample( uvNode ).r;
		const isSky = builder.renderer.reversedDepthBuffer === true
			? rawDepth.lessThanEqual( 1e-5 )
			: sampleDepth( uvNode ).greaterThanEqual( 1.0 );

		// Sky: march yok (classic SSGI depth>=1 discard; WebGPU reversed-Z far≈0).
		const computeGi = Fn( () => {

			const result = vec3( 0 ).toVar();

			If( isSky.not(), () => {

				const depth = sampleDepth( uvNode ).toVar();
				const viewPosition = getViewPosition( uvNode, depth, this._cameraProjectionMatrixInverse ).toVar();
				const viewNormal = sampleNormal( uvNode ).toVar();
				const viewDir = normalize( viewPosition.xyz.negate() ).toVar();

				const noiseOffset = spatialOffsets( screenCoordinate );
				const noiseDirection = interleavedGradientNoise( screenCoordinate );
				const noiseJitterIdx = this._temporalDirection.mul( 0.02 );
				const initialRayStep = fract( noiseOffset.add( this._temporalOffset ) ).add( rand( uvNode.add( noiseJitterIdx ).mul( 2 ).sub( 1 ) ) );

				const color = vec3( 0 );
				const ROTATION_COUNT = this.sliceCount.toConst();
				const GI_INTENSITY = this.giIntensity.toConst();
				const RADIUS = this.radius.toConst();

				Loop( { start: uint( 0 ), end: ROTATION_COUNT, type: 'uint', condition: '<' }, ( { i } ) => {

					const rotationAngle = mul( float( i ).add( noiseDirection ).add( this._temporalDirection ), PI.div( float( ROTATION_COUNT ) ) ).toConst();
					const sliceDir = vec3( vec2( cos( rotationAngle ), sin( rotationAngle ) ), 0 ).toConst();
					const slideDirTexelSize = sliceDir.xy.mul( float( 1 ).div( this._resolution ) ).toConst();

					const planeNormal = normalize( cross( sliceDir, viewDir ) ).toConst();
					const tangent = cross( viewDir, planeNormal ).toConst();
					const projectedNormal = viewNormal.sub( planeNormal.mul( dot( viewNormal, planeNormal ) ) ).toConst();
					const projectedNormalNormalized = normalize( projectedNormal ).toConst();

					const cos_n = clamp( dot( projectedNormalNormalized, viewDir ), - 1, 1 ).toConst();
					const n = sign( dot( projectedNormal, tangent ) ).negate().mul( acos( cos_n ) ).toConst();

					globalOccludedBitfield.assign( 0 );

					color.addAssign( horizonSampling( bool( true ), RADIUS, viewPosition, slideDirTexelSize, initialRayStep, uvNode, viewDir, viewNormal, n ) );
					color.addAssign( horizonSampling( bool( false ), RADIUS, viewPosition, slideDirTexelSize, initialRayStep, uvNode, viewDir, viewNormal, n ) );

				} );

				color.divAssign( float( ROTATION_COUNT ) );
				color.mulAssign( GI_INTENSITY );

				const maxLuminance = float( 7 ).toConst();
				const currentLuminance = luminance( color );
				const scale = currentLuminance.greaterThan( maxLuminance ).select( maxLuminance.div( currentLuminance ), float( 1 ) );
				color.mulAssign( scale );
				result.assign( color );

			} );

			return result;

		} );

		const gi = Fn( () => {

			const outColor = vec4( 0, 0, 0, 0 ).toVar();
			const strideF = this._strideUniform;
			const ix = floor( screenCoordinate.x );
			const iy = floor( screenCoordinate.y );
			const onGrid = abs( ix.sub( floor( ix.div( strideF ) ).mul( strideF ) ) ).lessThan( 0.5 )
				.and( abs( iy.sub( floor( iy.div( strideF ) ).mul( strideF ) ) ).lessThan( 0.5 ) );

			If( onGrid.not().or( isSky ), () => {

				Discard();

			} );

			If( onGrid.and( isSky.not() ), () => {

				outColor.assign( vec4( computeGi(), 1 ) );

			} );

			return outColor;

		} );

		this._material.fragmentNode = gi().context( builder.getSharedContext() );
		this._material.needsUpdate = true;

		const fill = Fn( () => {

			let needsFullGi = float( 0 ).greaterThan( 1 );
			const mode = this.adaptiveMode;
			if ( mode === 'edge' && this.depthNode && this.normalNode ) {

				needsFullGi = depthEdgeMask( {
					screenUv: uvNode,
					fullResolution: this.fullResolution,
					depth: this.depthNode,
					normal: this.normalNode,
					depthThreshold: this.edgeThreshold,
					thickness: this.edgeThickness,
					edgeKernel: this.edgeKernel,
					reversedDepth: this._reversedDepthUniform,
				} );

			} else if ( mode === 'refine' && this.depthNode && this.normalNode ) {

				needsFullGi = parentCellNeedsRefine( {
					screenCoordinate,
					fullResolution: this.fullResolution,
					coarseStride: this._strideUniform,
					depth: this.depthNode,
					normal: this.normalNode,
					depthThreshold: this.depthThreshold,
					normalThreshold: this.normalThreshold,
					cameraNear: this._cameraNear,
					cameraFar: this._cameraFar,
					reversedDepth: this._reversedDepthUniform,
				} );

			}

			const rgb = upsampleSparseGi( {
				giMap: this._ssgiRenderTarget.texture,
				screenCoordinate,
				fullResolution: this.fullResolution,
				stride: this._strideUniform,
				reversedDepth: this._reversedDepthUniform,
				depth: this.depthNode,
				normal: this.normalNode,
				depthThreshold: this.depthThreshold,
				normalThreshold: this.normalThreshold,
				minOkCount: this.minOkCount,
				cameraNear: this._cameraNear,
				cameraFar: this._cameraFar,
				screenUv: uvNode,
				debug: this.debugView,
				sky: isSky,
				needsFullGi,
				onFail: () => computeGi(),
			} );

			return vec4( rgb, 1 );

		} );

		this._fillMaterial.fragmentNode = fill().context( builder.getSharedContext() );
		this._fillMaterial.needsUpdate = true;

		return this._textureNode;

	}

	/**
	 * Frees internal resources. This method should be called
	 * when the effect is no longer required.
	 */
	dispose() {

		this._ssgiRenderTarget.dispose();
		this._fillTarget.dispose();
		this._material.dispose();
		this._fillMaterial.dispose();

	}

}

export default SparseGiNode;

/**
 * TSL function for creating a SSGI effect.
 *
 * @tsl
 * @function
 * @param {TextureNode} beautyNode - The texture node that represents the input of the effect.
 * @param {TextureNode} depthNode - A texture node that represents the scene's depth.
 * @param {TextureNode} normalNode - A texture node that represents the scene's normals.
 * @param {Camera} camera - The camera the scene is rendered with.
 * @returns {SSGINode}
 */
export const sparseGi = ( beautyNode, depthNode, normalNode, camera ) =>
	new SparseGiNode( convertToTexture( beautyNode ), depthNode, normalNode, camera );
